import { DataPersistence } from '../../utils/data.persistance';
import { AggregatesState } from './aggregates.reducer';
import { Effect } from '@ngrx/effects';
import {
  AggregatesActionTypes,
  BankAggregateLoaded,
  DispensaryAggregateLoaded,
  DispensaryAggregatesLoaded,
  FincenDepositAggregateLoaded,
  FincenReportAggregateLoaded,
  LoadBankAggregate,
  LoadDispensaryAggregate,
  LoadDispensaryAggregates,
  LoadFincenDepositAggregate,
  LoadFincenReportAggregate,
  LoadSalesAggregates,
  LoadSalesAggregate,
  LoadSarReportPeriodAggregate,
  SalesAggregateLoaded,
  SalesAggregatesLoaded,
  SarReportPeriodAggregateLoaded,
} from './aggregates.actions';
import { Injectable } from '@angular/core';
import {
  AggregateResponsePayload,
  BankAggregate,
  DispensaryAggregate,
  FincenDepositAggregate,
  FincenReportAggregate,
  SalesAggregate,
} from '../../typings/aggregates';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AddError } from '../error';
import { DefaultService } from '@gcv/generated-services';

@Injectable({
  providedIn: 'root',
})
export class AggregatesEffect {
  @Effect() loadBankAggregate$ = this.dataPersistence.fetch(AggregatesActionTypes.LoadBankAggregate, {
    run: (action: LoadBankAggregate, state: AggregatesState) => {
      return this.sdk.aggregatesCompanyIdTypeGet(action.payload.type, action.payload.companyId).pipe(
        map(
          (res: AggregateResponsePayload) =>
            new BankAggregateLoaded({
              id: res.company_id,
              data: res.data as BankAggregate,
            })
        )
      );
    },
    onError: err => {
      return new AddError({ action: AggregatesActionTypes.LoadBankAggregate, message: err });
    },
  });

  @Effect() loadSalesAggregates$ = this.dataPersistence.fetch(AggregatesActionTypes.LoadSalesAggregates, {
    run: (action: LoadSalesAggregates, state: AggregatesState) => {
      return forkJoin(
        action.payload.companyIds.map(companyId => {
          return this.sdk.aggregatesCompanyIdTypeGet(action.payload.type, companyId).pipe(
            map((res: AggregateResponsePayload) => {
              return {
                id: res.company_id,
                data: res.data as SalesAggregate,
              };
            })
          );
        })
      ).pipe(
        map(
          (res: { id: string; data: SalesAggregate }[]) =>
            new SalesAggregatesLoaded({
              aggregates: res,
            })
        )
      );
    },
    onError: err => {
      return new AddError({ action: AggregatesActionTypes.LoadSalesAggregates, message: err });
    },
  });

  @Effect() loadSalesAggregate$ = this.dataPersistence.fetch(AggregatesActionTypes.LoadSalesAggregate, {
    run: (action: LoadSalesAggregate, state: AggregatesState) => {
      return this.sdk.aggregatesCompanyIdTypeGet(action.payload.type, action.payload.companyId).pipe(
        map(
          (res: AggregateResponsePayload) =>
            new SalesAggregateLoaded({
              id: res.company_id,
              data: res.data as SalesAggregate,
            })
        )
      );
    },
    onError: err => {
      return new AddError({ action: AggregatesActionTypes.LoadSalesAggregate, message: err });
    },
  });

  @Effect() loadFincenDepositAggregate$ = this.dataPersistence.fetch(AggregatesActionTypes.LoadFincenDepositAggregate, {
    run: (action: LoadFincenDepositAggregate, state: AggregatesState) => {
      return this.sdk.aggregatesCompanyIdTypeGet(action.payload.type, action.payload.companyId).pipe(
        map(
          (res: AggregateResponsePayload) =>
            new FincenDepositAggregateLoaded({
              id: res.company_id,
              data: res.data as FincenDepositAggregate,
            })
        )
      );
    },
    onError: err => {
      return new AddError({ action: AggregatesActionTypes.LoadFincenDepositAggregate, message: err });
    },
  });

  @Effect() loadFincenReportAggregate$ = this.dataPersistence.fetch(AggregatesActionTypes.LoadFincenReportAggregate, {
    run: (action: LoadFincenReportAggregate, state: AggregatesState) => {
      return this.sdk
        .aggregatesCompanyIdTypeGet(action.payload.type, action.payload.companyId)
        .pipe(
          map((res: AggregateResponsePayload) => new FincenReportAggregateLoaded(res.data as FincenReportAggregate[]))
        );
    },
    onError: err => {
      return new AddError({ action: AggregatesActionTypes.LoadFincenReportAggregate, message: err });
    },
  });

  @Effect() loadDispensaryAggregate$ = this.dataPersistence.fetch(AggregatesActionTypes.LoadDispensaryAggregate, {
    run: (action: LoadDispensaryAggregate, state: AggregatesState) => {
      return this.sdk.aggregatesCompanyIdTypeGet(action.payload.type, action.payload.companyId).pipe(
        map(
          (res: AggregateResponsePayload) =>
            new DispensaryAggregateLoaded({
              id: res.company_id,
              data: res.data as DispensaryAggregate,
            })
        )
      );
    },
    onError: err => {
      return new AddError({ action: AggregatesActionTypes.LoadDispensaryAggregate, message: err });
    },
  });

  @Effect() loadDispensaryAggregates$ = this.dataPersistence.fetch(AggregatesActionTypes.LoadDispensaryAggregates, {
    run: (action: LoadDispensaryAggregates, state: AggregatesState) => {
      return forkJoin(
        action.payload.companyIds.map(companyId => {
          return this.sdk.aggregatesCompanyIdTypeGet(action.payload.type, companyId).pipe(
            map((res: AggregateResponsePayload) => {
              return {
                id: res.company_id,
                data: res.data as DispensaryAggregate,
              };
            })
          );
        })
      ).pipe(
        map(
          (res: { id: string; data: DispensaryAggregate }[]) =>
            new DispensaryAggregatesLoaded({
              aggregates: res,
            })
        )
      );
    },
    onError: err => {
      return new AddError({ action: AggregatesActionTypes.LoadDispensaryAggregates, message: err });
    },
  });

  @Effect() loadSarReportPeriodAggregate$ = this.dataPersistence.fetch(
    AggregatesActionTypes.LoadSarReportPeriodAggregate,
    {
      run: (action: LoadSarReportPeriodAggregate, state: AggregatesState) => {
        return this.sdk.fincenReportsSarPeriodsBankIdGet(action.payload.companyId).pipe(
          map((res: any[]) => {
            return new SarReportPeriodAggregateLoaded({
              data: res as DispensaryAggregate[],
            });
          })
        );
      },
      onError: (action, error) => {
        const payload = { status: error.response.status, message: error.toJSON().message };
        return new AddError({ action: AggregatesActionTypes.LoadSarReportPeriodAggregate, message: payload });
      },
    }
  );

  constructor(private sdk: DefaultService, private dataPersistence: DataPersistence<AggregatesState>) {}
}
